import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import CalendarComponent from '../../../Calendar/CalendarComponent';
import '../Payout/PayoutDetail/AddTransactionHistory/AddTransactionHistory.css';

// Matches the house Add/Edit Transaction History modal on Payout, so the two
// transaction forms in the admin panel look and behave the same.
const StatusColor = {
  Failed: { color: '#E14949', bgcolor: '#FFF2F2', value: 'Failed' },
  Success: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Success' },
};

/** dd/mm/yyyy -> Date, the format the house calendar hands back. */
const parseDisplayDate = value => {
  if (!value) return null;
  const [dd, mm, yyyy] = value.split('/');
  const parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Date -> "YYYY-MM-DDT00:00:00", with no timezone suffix.
 *
 * toISOString() would convert local midnight to UTC and move the day back for
 * any timezone east of Greenwich -- picking 23 Oct in Doha stored 22 Oct. The
 * column is naive UTC, and the admin is recording a calendar date, so send
 * exactly the day they chose.
 */
const toNaiveIso = date => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00`;
};

const toDisplayDate = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
};

/**
 * Records what the bank did with an EFT.
 *
 * A success needs a reference -- a transferred EFT with nothing to reconcile
 * against is the state this feature exists to prevent, and the API rejects it.
 * A failure may legitimately have none, so the field is only required above.
 */
export function TransactionDetailsModal({ open = false, transfer, onClose, onSubmit, isSaving }) {
  const [form, setForm] = useState({ transactionId: '', date: '', status: '' });
  const [statusOpen, setStatusOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Recording happens after the fact, but never for a date in the future.
  const maxSelectableDate = useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }, []);

  const isEdit = Boolean(transfer?.bankReference);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setStatusOpen(false);
      }
    };
    if (statusOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusOpen]);

  useEffect(() => {
    if (!open) return;
    setForm({
      transactionId: transfer?.bankReference || '',
      date: toDisplayDate(transfer?.transferredAtRaw),
      status:
        transfer?.status === 'failed'
          ? 'Failed'
          : transfer?.status === 'transferred'
            ? 'Success'
            : '',
    });
    setStatusOpen(false);
    setCalendarOpen(false);
  }, [open, transfer]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleDateSelect = date => {
    const selected = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (selected > maxSelectableDate) {
      toast.error('Transaction date cannot be in the future');
      return;
    }
    setForm(prev => ({ ...prev, date: date.toLocaleDateString('en-GB') }));
    setCalendarOpen(false);
  };

  const referenceMissing = form.status === 'Success' && !form.transactionId.trim();
  const isFormValid = Boolean(form.status) && !referenceMissing && !isSaving;

  const handleSave = () => {
    if (!isFormValid) return;
    onSubmit({
      status: form.status.toLowerCase(),
      reference: form.transactionId.trim(),
      transactionDate: toNaiveIso(parseDisplayDate(form.date)),
    });
  };

  if (!open) return null;

  return (
    <div className="payoutth-overlay">
      <div className="payoutth-modal">
        <div className="payoutth-header">
          <div className="payoutth-title">
            {isEdit ? 'Edit Transaction Details' : 'Add Transaction Details'}
          </div>
          <button
            type="button"
            className="payoutth-close-btn"
            onClick={onClose}
            aria-label="Close"
          />
        </div>

        <div className="payoutth-requested-payout">
          Transfer Total: <span className="payoutth-amount">{transfer?.totalAmount} QAR</span>
        </div>

        <div className="payoutth-table">
          <table className="payoutth-table-container">
            <thead>
              <tr>
                <th className="payoutth-th">Transaction ID</th>
                <th className="payoutth-th">Transaction Date</th>
                <th className="payoutth-th">Transfer Amount</th>
                <th className="payoutth-th">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="payoutth-td">
                  <input
                    className="payoutth-input"
                    placeholder="XXXX"
                    // The column is VARCHAR(100); longer is a 422 from the API.
                    maxLength={100}
                    value={form.transactionId}
                    onChange={event => handleChange('transactionId', event.target.value)}
                    style={{ borderColor: referenceMissing ? '#E14949' : undefined }}
                  />
                  {referenceMissing && (
                    <div className="payoutth-validation-error" style={{ marginTop: '6px' }}>
                      Required when the transaction succeeded
                    </div>
                  )}
                </td>
                <td className="payoutth-td">
                  <div className="payoutth-date-container">
                    <input
                      className="payoutth-input"
                      placeholder="DD/MM/YYYY"
                      value={form.date}
                      onClick={() => setCalendarOpen(true)}
                      readOnly
                    />
                    {calendarOpen && (
                      <CalendarComponent
                        isOpen={calendarOpen}
                        onClose={() => setCalendarOpen(false)}
                        onDateSelect={handleDateSelect}
                        maxDate={maxSelectableDate}
                        selectedDate={parseDisplayDate(form.date)}
                      />
                    )}
                  </div>
                </td>
                <td className="payoutth-td">
                  {/* The EFT total is the sum of its claimed payments; it is
                      never typed in, so it is shown read-only. */}
                  <input
                    className="payoutth-input"
                    value={transfer?.totalAmount || ''}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </td>
                <td className="payoutth-td">
                  <div className="payoutth-select" ref={dropdownRef}>
                    <button
                      type="button"
                      className="payoutth-select-trigger"
                      onClick={() => setStatusOpen(open_ => !open_)}
                      style={{
                        color: StatusColor[form.status]?.color || '#14171f',
                      }}
                    >
                      {form.status || 'Select'}
                      <span className="payoutth-caret" />
                    </button>
                    {statusOpen && (
                      <div className="payoutth-select-menu">
                        {Object.entries(StatusColor).map(([key, config]) => (
                          <div
                            key={key}
                            className="payoutth-option"
                            style={{ color: config.color }}
                            onClick={() => {
                              handleChange('status', config.value);
                              setStatusOpen(false);
                            }}
                          >
                            {config.value}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="payoutth-footer">
          <div className="payoutth-footer-buttons">
            <button type="button" className="payoutth-btn payoutth-btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="payoutth-btn payoutth-btn-primary"
              onClick={handleSave}
              disabled={!isFormValid}
              style={{
                opacity: isFormValid ? 1 : 0.6,
                cursor: isFormValid ? 'pointer' : 'not-allowed',
              }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
